import {
  ActionIcon,
  Anchor,
  Box,
  Center,
  Flex,
  Group,
  Image,
  Indicator,
  Menu,
  Paper,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { IconBell, IconChevronDown } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useNotifications } from "../hooks/useNotifications";
import {
  useOwnerProfileDisplay,
  useTrainerProfileDisplay,
} from "../hooks/useProfileDisplay";
import { ownerProfileNavLinks } from "../pages/owner/profile/Profile";
import { trainerProfileNavLinks } from "../pages/trainer/AccountSettings";
import {
  type MinArray,
  type NavItem,
  UniversalRoutes,
  useNavigator,
} from "../routers/routes";
import { useUser } from "../UserContext";
import { useSignOut } from "../utils/signout";
import { Portrait } from "./Portrait";

export const TopNavigationBar = (props: { icons: NavItem[] }) => {
  const { notifications, unreadCount } = useNotifications();
  const { icons } = props;
  const [location, setLocation] = useLocation();
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const { role } = useUser();
  const ownerProfile = useOwnerProfileDisplay();
  const trainerProfile = useTrainerProfileDisplay();
  const { navigator } = useNavigator();

  const profileNavLinks =
    role === "trainer" ? trainerProfileNavLinks : ownerProfileNavLinks;
  const signOut = useSignOut();

  const profile =
    role === "trainer" ? trainerProfile.profile : ownerProfile.profile;

  const displayProfile = profile ?? {
    id: "fake-id",
    firstName: "?",
    lastName: "",
    portraitFullPath: null,
    inviteCode: "...",
  };

  // Generate display name based on role
  const getDisplayName = () => {
    if (!profile) return "...";

    if (role === "trainer") {
      return profile.firstName; // Only first name for trainers
    } else {
      // For owners, show "FirstName and DogName"
      const ownerData = ownerProfile.profile;
      if (ownerData?.dogs && ownerData.dogs.length > 0) {
        return `${profile.firstName} and ${ownerData.dogs[0].name}`;
      }
      return profile.firstName; // Fallback to just first name if no dogs
    }
  };

  useEffect(() => {
    if (
      notifications?.some(
        (notification) =>
          !notification.isRead && notification.type === "CHAT_MESSAGE",
      ) &&
      !location.startsWith("/chats")
    ) {
      setHasNewMessage(true);
    }
  }, [notifications, location.startsWith]);

  useEffect(() => {
    if (location.startsWith("/chats") && hasNewMessage) {
      setHasNewMessage(false);
    }
  }, [location, hasNewMessage]);

  const isActiveRoute = (currentLocation: string, route: string | MinArray) => {
    if (route === "/") {
      return currentLocation === "/";
    }
    if (Array.isArray(route)) {
      return route.some((r) => currentLocation.startsWith(r));
    }
    return currentLocation.startsWith(route);
  };

  const firstLocation = (route: string | MinArray) => {
    if (Array.isArray(route)) {
      return route[0];
    }
    return route;
  };

  // Render navigation links (desktop)
  const links = icons.map((icon) => {
    const isActive = isActiveRoute(location, icon.route);

    return (
      <Anchor
        key={icon.label}
        variant="transparent"
        fw={isActive ? 700 : 500}
        c={isActive ? "blue" : "dark"}
        href={firstLocation(icon.route)}
        onClick={(event) => {
          event.preventDefault();
          setLocation(firstLocation(icon.route));
        }}
        style={{
          textDecoration: "none",
        }}
      >
        <Group gap={"xs"}>
          <Indicator
            color="dark-accent.3"
            position="top-end"
            size={8}
            offset={0}
            disabled={!(icon.chat && hasNewMessage)}
          >
            <Text fw={500}>{icon.label}</Text>
          </Indicator>
        </Group>
      </Anchor>
    );
  });

  return (
    <Paper
      p="md"
      bg="white"
      style={{
        borderBottom: `1px solid var(--mantine-color-gray-3)`,
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}
    >
      <Flex justify="space-between" align="center">
        <Group
          align="center"
          gap="xs"
          style={{
            flexWrap: "nowrap",
            cursor: "pointer",
          }}
          onClick={() => setLocation("/")}
        >
          <Image
            radius="sm"
            src="/favicons/favicon-96x96.png"
            alt="🐾"
            width={36}
            height={36}
          />
          <Text
            size="xl"
            fw={700}
            c="black"
            style={{ display: "inline-block" }}
          >
            Woofapy
          </Text>
        </Group>

        <Group gap={30}>{links}</Group>

        <Group gap={15}>
          <ActionIcon
            variant="transparent"
            size="lg"
            color="gray"
            onClick={navigator({
              path: UniversalRoutes.NOTIFICATIONS.LIST,
            })}
          >
            <Indicator
              size={20}
              offset={5}
              position="top-end"
              color="dark-accent.3"
              withBorder
              disabled={!unreadCount}
              label={
                <Text size="xs" fw={"bold"}>
                  {unreadCount}
                </Text>
              }
            >
              <Box h={24} w={24}>
                <IconBell size={24} />
              </Box>
            </Indicator>
          </ActionIcon>

          <Menu
            trigger="hover"
            openDelay={100}
            closeDelay={200}
            position="bottom-end"
            withinPortal
          >
            <Menu.Target>
              <Group
                gap={7}
                style={{ cursor: "pointer" }}
                onClick={() => setLocation(UniversalRoutes.PROFILE.HOME)}
              >
                <Portrait profile={displayProfile} size="sm" />
                <Text fw={500} lh={1} mr={3}>
                  {getDisplayName()}
                </Text>
                <IconChevronDown size={12} stroke={1.5} />
              </Group>
            </Menu.Target>
            <Menu.Dropdown>
              {profileNavLinks.map((link) => (
                <Menu.Item
                  fz="lg"
                  key={link.label}
                  leftSection={<link.icon size={18} />}
                  onClick={() => {
                    if (link.signOut) {
                      signOut();
                    } else {
                      setLocation(link.route);
                    }
                  }}
                >
                  {link.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Flex>
    </Paper>
  );
};

export const BottomNavigationBar = (props: { icons: NavItem[] }) => {
  const { notifications } = useNotifications();

  const sizing = {
    stroke: 1.5,
    size: 30,
    buttonSize: 60,
  };

  const { icons } = props;
  const [location, setLocation] = useLocation();

  const [hasNewMessage, setHasNewMessage] = useState(false);

  useEffect(() => {
    if (
      notifications?.some(
        (notification) =>
          !notification.isRead && notification.type === "CHAT_MESSAGE",
      ) &&
      !location.startsWith("/chats")
    ) {
      setHasNewMessage(true);
    }
  }, [notifications, location.startsWith]);

  useEffect(() => {
    if (location.startsWith("/chats") && hasNewMessage) {
      setHasNewMessage(false);
    }
  }, [location, hasNewMessage]);

  const isActiveRoute = (currentLocation: string, route: string | MinArray) => {
    if (route === "/") {
      return currentLocation === "/";
    }
    if (Array.isArray(route)) {
      return route.some((r) => currentLocation.startsWith(r));
    }
    return currentLocation.startsWith(route);
  };

  const firstLocation = (route: string | string[]) => {
    if (Array.isArray(route)) {
      return route[0] ?? "";
    }
    return route;
  };

  const displayIcons = icons.map((icon) => {
    const isActive = isActiveRoute(location, icon.route);
    return {
      ...icon,
      isActive,
      displayIcon:
        isActive && icon.selectedIcon ? icon.selectedIcon : icon.icon,
    };
  });

  return (
    <Paper
      radius={0}
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        width: "100%",
        borderTop: `2px solid lightgray`,
      }}
      shadow="md"
    >
      <Center>
        <SimpleGrid w="100%" cols={icons.length} spacing={0}>
          {displayIcons.map((icon) => (
            <Center key={icon.label}>
              <ActionIcon
                onClick={() => setLocation(firstLocation(icon.route))}
                variant="transparent"
                w="100px"
                h={sizing.buttonSize}
                color={icon.isActive ? undefined : "gray.9"}
              >
                <Stack gap={0}>
                  <Center>
                    <Indicator
                      color="dark-accent.3"
                      position="top-start"
                      inline
                      offset={5}
                      size={10}
                      disabled={!(icon.chat && hasNewMessage)}
                    >
                      <icon.displayIcon
                        stroke={sizing.stroke}
                        size={sizing.size}
                      />
                    </Indicator>
                  </Center>

                  <Center>
                    <Text size="sm">{icon.label}</Text>
                  </Center>
                </Stack>
              </ActionIcon>
            </Center>
          ))}
        </SimpleGrid>
      </Center>
    </Paper>
  );
};
